/**
 * Ines Maths — Cart Notification Recommendations
 * Fetches Rebuy upsell + cross-sell recommendations after add-to-cart
 * and renders compact product cards inside the notification dropdown.
 *
 * Data sources: 285707 (cart_page_Upsell), 285710 (Cart-page_cross-sell)
 * Widgets: 274163 (upsell), 274449 (cross-sell)
 *
 * Bug fixes (2026-03-24):
 *  1. Discount badge: fetches widget settings via REST API (not window.Rebuy.widgets)
 *  2. Add button: uses Rebuy.Cart.addItem() instead of raw fetch (keeps SDK state in sync)
 *  3. Upsell swap: removeItem → addItem for upsell recos (cross-sell just adds)
 */
(function () {
  'use strict';

  /* ── Config ── */
  var REBUY_API_KEY = '38f84c45e94d8d5952d29188037db49a41dd1bf4';
  var DS_UPSELL = '285707';
  var DS_CROSSSELL = '285710';
  var WIDGET_UPSELL = '274163';
  var WIDGET_CROSSSELL = '274449';
  var MAX_RECOS = 3;
  var DEBOUNCE_MS = 300;
  var DISCOUNT_PCT_FALLBACK = 15; /* last resort if API fetch fails */
  var AUTO_CLOSE_MS = 5000;       /* auto-dismiss after 5s of no interaction */
  var POST_ADD_DELAY_MS = 600;    /* show "✓ Ajouté" for 0.6s before fade */
  var FADE_MS = 300;              /* fade-out duration */

  var debounceTimer = null;
  var currentFetchId = 0; /* prevents stale renders from slow responses */
  var _isAdding = false; /* guard: prevents fetchAndRender during add flow */
  var autoCloseTimer = null;

  /* ── Widget discount settings cache ── */
  var widgetDiscountCache = {}; /* { widgetId: { type, amount } } */
  var widgetSettingsFetched = false;

  /**
   * Fetch widget discount settings via Rebuy REST API.
   * GET /api/v1/widgets/settings?id={widget_id}&cache_key={cache_key}
   * Called once, results cached for the session.
   */
  function fetchWidgetSettings() {
    if (widgetSettingsFetched) return Promise.resolve();
    widgetSettingsFetched = true;

    var cacheKey = '';
    if (window.Rebuy && window.Rebuy.shop && window.Rebuy.shop.cache_key) {
      cacheKey = window.Rebuy.shop.cache_key;
    }

    var widgetIds = [WIDGET_UPSELL, WIDGET_CROSSSELL];
    var promises = widgetIds.map(function (wid) {
      var url = 'https://rebuyengine.com/api/v1/widgets/settings?id=' + wid +
        '&key=' + REBUY_API_KEY +
        (cacheKey ? '&cache_key=' + cacheKey : '');

      return fetch(url)
        .then(function (r) { return r.json(); })
        .then(function (resp) {
          var settings = resp && resp.data && resp.data.settings;
          if (settings && settings.discount && settings.discount.amount > 0) {
            widgetDiscountCache[wid] = settings.discount;
          }
        })
        .catch(function () { /* silent — fallback to DISCOUNT_PCT_FALLBACK */ });
    });

    return Promise.all(promises);
  }

  /**
   * Get discount config for a widget — tries cache first, then fallback.
   */
  function getWidgetDiscount(widgetId) {
    if (widgetDiscountCache[widgetId]) return widgetDiscountCache[widgetId];
    /* Fallback: constant percentage (matches Shopify Functions discount) */
    return { type: 'percentage', amount: DISCOUNT_PCT_FALLBACK };
  }

  /* ── Subscribe to cart updates ── */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event.source === 'cart-notification-reco') return;
      if (_isAdding) return; /* skip during our own add flow */
      debouncedFetch();
    });
  }

  function debouncedFetch() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(fetchAndRender, DEBOUNCE_MS);
  }

  /* ── Main fetch + render pipeline ── */
  function fetchAndRender() {
    var container = document.getElementById('cart-notification-reco');
    if (!container) return;
    if (_isAdding) return;

    var fetchId = ++currentFetchId;

    /* Ensure widget settings are loaded before first render */
    fetchWidgetSettings().then(function () {
      return getCartProductIds();
    }).then(function (cartIds) {
      if (!cartIds.length) {
        container.innerHTML = '';
        return;
      }

      var cartIdSet = {};
      cartIds.forEach(function (id) { cartIdSet[id] = true; });

      return Promise.all([
        fetchDS(DS_UPSELL, cartIds),
        fetchDS(DS_CROSSSELL, cartIds)
      ]).then(function (results) {
        /* Abort if a newer fetch has started */
        if (fetchId !== currentFetchId) return;

        var allProducts = [];
        var seenIds = {};

        /* Merge + dedup — upsell first (higher priority) */
        results.forEach(function (dsResult, dsIndex) {
          var widgetId = dsIndex === 0 ? WIDGET_UPSELL : WIDGET_CROSSSELL;
          dsResult.forEach(function (product) {
            if (!seenIds[product.id] && !cartIdSet[product.id]) {
              seenIds[product.id] = true;
              product._widgetId = widgetId;
              allProducts.push(product);
            }
          });
        });

        /* Sort by price descending so the 2 most expensive always show first
           (important on mobile where the 3rd card is hidden via CSS) */
        allProducts.sort(function (a, b) {
          var priceA = (a.variants && a.variants[0] && parseFloat(a.variants[0].price)) || 0;
          var priceB = (b.variants && b.variants[0] && parseFloat(b.variants[0].price)) || 0;
          return priceB - priceA;
        });

        var recos = allProducts.slice(0, MAX_RECOS);
        if (recos.length > 0) {
          renderRecos(container, recos);
        } else {
          container.innerHTML = '';
        }
      });
    });
  }

  /* ── Rebuy Data Source API ── */
  function fetchDS(dsId, productIds) {
    var url = 'https://rebuyengine.com/api/v1/custom/id/' + dsId +
      '?key=' + REBUY_API_KEY +
      '&shopify_product_ids=' + productIds.join(',') +
      '&fields=id,title,handle,image,variants,tags';

    return fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (resp) { return resp.data || []; })
      .catch(function () { return []; });
  }

  /* ── Cart state ── */
  function getCartProductIds() {
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        return cart.items.map(function (item) { return item.product_id; });
      })
      .catch(function () { return []; });
  }

  /* ── Discount logic ── */
  function applyDiscount(widgetId, rawPrice, rawCompareAt) {
    var disc = getWidgetDiscount(widgetId);

    var basePrice = parseFloat(rawPrice) || 0;
    if (basePrice <= 0) return { price: rawPrice, compareAt: rawCompareAt };

    var discounted;
    if (disc.type === 'percentage') {
      discounted = basePrice * (1 - disc.amount / 100);
    } else if (disc.type === 'fixed') {
      discounted = Math.max(0, basePrice - disc.amount);
    } else {
      return { price: rawPrice, compareAt: rawCompareAt };
    }

    discounted = Math.round(discounted * 100) / 100;
    if (discounted >= basePrice) return { price: rawPrice, compareAt: rawCompareAt };

    return { price: discounted, compareAt: basePrice };
  }

  /* ── Price formatting (Rebuy returns euros, not cents) ── */
  function formatPrice(price) {
    var euros = parseFloat(price).toFixed(2).replace('.', ',');
    return euros + ' €';
  }

  /* ── Parse upgrades-from:handle tags ── */
  function parseUpgradeFromTags(tags) {
    if (!tags) return [];
    if (typeof tags === 'string') {
      tags = tags.split(',').map(function (t) { return t.trim().toLowerCase(); });
    } else if (Array.isArray(tags)) {
      tags = tags.map(function (t) { return (t || '').toLowerCase(); });
    } else {
      return [];
    }

    var handles = [];
    tags.forEach(function (tag) {
      if (tag.indexOf('upgrades-from:') === 0) {
        handles.push(tag.replace('upgrades-from:', '').trim());
      }
    });
    return handles;
  }

  /* ── Render recommendation cards ── */
  function renderRecos(container, products) {
    /* Heading adapts to recommendation type */
    var hasUpsell = products.some(function (p) { return p._widgetId === WIDGET_UPSELL; });
    var hasCrossSell = products.some(function (p) { return p._widgetId === WIDGET_CROSSSELL; });
    var heading = hasCrossSell ? 'Les élèves prennent aussi' : 'Passe au pack complet';
    if (hasUpsell && hasCrossSell) heading = 'Pour cartonner encore plus';

    var html = '<div class="ines-notif-reco__heading">' + heading + '</div>';
    html += '<div class="ines-notif-reco__list">';

    products.forEach(function (product) {
      var variant = product.variants && product.variants[0];
      if (!variant) return;

      var variantId = variant.id;
      var rawPrice = variant.price;
      var rawCompareAt = variant.compare_at_price;
      var widgetId = product._widgetId || WIDGET_UPSELL;

      /* Apply widget-level discount */
      var pricing = applyDiscount(widgetId, rawPrice, rawCompareAt);

      var imageUrl = product.image ? product.image.src : '';
      if (imageUrl) {
        /* Shopify CDN resize to 140px wide for portrait card */
        imageUrl = imageUrl.replace(/\.(?:jpg|jpeg|png|gif|webp)/i, function (ext) {
          return '_140x' + ext;
        });
      }

      var hasDiscount = pricing.compareAt && pricing.compareAt > pricing.price;

      /* Store product data for swap logic (Bug 3) */
      var productJson = JSON.stringify({
        id: product.id,
        tags: product.tags || [],
        handle: product.handle,
        title: product.title
      });

      html += '<div class="ines-notif-reco__card" role="listitem">';

      /* Image */
      html += '<a href="/products/' + product.handle + '" class="ines-notif-reco__img-link">';
      html += '<img src="' + imageUrl + '" alt="' + escapeHtml(product.title) + '" class="ines-notif-reco__img" width="70" height="90" loading="lazy" />';
      html += '</a>';

      /* Info */
      html += '<div class="ines-notif-reco__info">';
      html += '<a href="/products/' + product.handle + '" class="ines-notif-reco__title">' + escapeHtml(product.title) + '</a>';
      html += '<div class="ines-notif-reco__price">';
      html += '<span class="ines-notif-reco__price-current">' + formatPrice(pricing.price) + '</span>';
      if (hasDiscount) {
        html += '<span class="ines-notif-reco__price-compare">' + formatPrice(pricing.compareAt) + '</span>';
        var disc = getWidgetDiscount(widgetId);
        var pct = disc && disc.type === 'percentage' && disc.amount > 0 ? Math.round(disc.amount) : 0;
        if (pct > 0) {
          html += '<span class="ines-notif-reco__discount-badge">-' + pct + '%</span>';
        }
      }
      html += '</div>';
      html += '</div>';

      /* Add button — stores product JSON for swap logic */
      html += '<button type="button" class="ines-notif-reco__btn"';
      html += ' data-variant-id="' + variantId + '"';
      html += ' data-widget-id="' + widgetId + '"';
      html += " data-product-json='" + escapeAttr(productJson) + "'";
      html += '>+ Ajouter</button>';

      html += '</div>';
    });

    html += '</div>';
    container.innerHTML = html;

    /* Staggered fade-in after DOM paint */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        container.classList.add('ines-notif-reco--visible');
      });
    });

    /* Update CTA with current cart count */
    updateCtaCount();

    /* Start auto-close timer (pauses on hover) */
    initAutoCloseListeners();
    startAutoClose();

    /* Bind add-to-cart buttons */
    container.querySelectorAll('.ines-notif-reco__btn').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        e.preventDefault();
        handleAdd(btn);
      });
    });
  }

  /* ── Add to cart from recommendation ── */
  function handleAdd(btn) {
    if (_isAdding) return;

    var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
    var widgetId = btn.getAttribute('data-widget-id') || WIDGET_UPSELL;
    var productData = {};
    try { productData = JSON.parse(btn.getAttribute('data-product-json') || '{}'); } catch (e) { /* */ }

    var isUpsell = widgetId === WIDGET_UPSELL;

    btn.disabled = true;
    btn.textContent = 'Ajout...';
    _isAdding = true;

    /* For upsells: swap (remove original → add upgrade)
       For cross-sells: just add alongside */
    if (isUpsell) {
      handleUpsellSwap(btn, variantId, widgetId, productData);
    } else {
      addViaRebuySDK(btn, variantId, widgetId);
    }
  }

  /**
   * Cross-sell: simple add via Rebuy SDK.
   */
  function addViaRebuySDK(btn, variantId, widgetId) {
    var done = false;

    function finish() {
      if (done) return;
      done = true;
      clearTimeout(fallbackTimer);
      document.removeEventListener('rebuy:cart.add', onAdded);
      onAddComplete(btn);
    }

    function onAdded() { finish(); }

    /* Timeout fallback in case rebuy:cart.add never fires */
    var fallbackTimer = setTimeout(finish, 3000);
    document.addEventListener('rebuy:cart.add', onAdded);

    Rebuy.Cart.addItem({
      id: variantId,
      quantity: 1,
      properties: {
        _source: 'Rebuy',
        _widget_id: String(widgetId),
        _attribution: 'Rebuy Cart Notification'
      }
    });
  }

  /**
   * Upsell: swap — remove original product from cart, then add upgrade.
   * Reads upgrades-from:handle tags to find which cart item to remove.
   * Uses /cart.js + /cart/update.js (Rebuy.Cart.items() is empty on product pages).
   */
  function handleUpsellSwap(btn, variantId, widgetId, productData) {
    var upgradeFromHandles = parseUpgradeFromTags(productData.tags);

    /* No swap tags → treat as simple add */
    if (upgradeFromHandles.length === 0) {
      addViaRebuySDK(btn, variantId, widgetId);
      return;
    }

    /* Find matching cart item via Shopify /cart.js (Rebuy.Cart.items() is empty on product pages) */
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var updates = {};
        var found = false;

        cart.items.forEach(function (item) {
          if (item.product_id == productData.id) return; /* skip the upsell product itself */
          if (upgradeFromHandles.indexOf((item.handle || '').toLowerCase()) !== -1) {
            updates[item.key] = 0;
            found = true;
          }
        });

        if (!found) {
          /* No matching item in cart — just add */
          addViaRebuySDK(btn, variantId, widgetId);
          return;
        }

        /* Remove matching items, then add upgrade */
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        }).then(function () {
          addViaRebuySDK(btn, variantId, widgetId);
        });
      })
      .catch(function () {
        /* On error, still try to add the upsell */
        addViaRebuySDK(btn, variantId, widgetId);
      });
  }

  /**
   * Post-add UI updates — shared by cross-sell add and upsell swap.
   * Shows "✓ Ajouté" for 1s, then fades out the entire notification.
   */
  function onAddComplete(btn) {
    btn.textContent = '✓ Ajouté';
    btn.classList.add('ines-notif-reco__btn--added');

    /* Update cart icon bubble + CTA count */
    refreshCartBubble();
    updateCtaCount();

    /* Publish cartUpdate so Dawn theme components stay in sync */
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-notification-reco'
      });
    }

    /* Cancel auto-close timer — post-add flow takes over */
    clearAutoCloseTimer();
    setTimeout(fadeOutAndClose, POST_ADD_DELAY_MS);
  }

  /* ── Fade-out & auto-close ── */

  /**
   * Fade out the entire notification (product info + recos), then close.
   */
  function fadeOutAndClose() {
    clearAutoCloseTimer();
    var notif = document.getElementById('cart-notification');
    if (!notif || notif.classList.contains('cart-notification--fade-out')) return;

    notif.classList.add('cart-notification--fade-out');

    /* Fallback in case transitionend doesn't fire (reduced motion, etc.) */
    var fallback = setTimeout(onFadeComplete, FADE_MS + 100);

    notif.addEventListener('transitionend', function handler(e) {
      if (e.target !== notif) return; /* ignore child element transitions */
      clearTimeout(fallback);
      notif.removeEventListener('transitionend', handler);
      onFadeComplete();
    });
  }

  function onFadeComplete() {
    var notif = document.getElementById('cart-notification');
    if (notif) {
      /*
       * Skip Dawn's slide-up animation after our fade.
       * Dawn's close() removes .active, which triggers a transform transition
       * (translateY(0) → translateY(-100%)) via the .animate class — causing
       * a ghost "afterimage" sliding up. Instead:
       * 1. Remove .animate to disable Dawn's transition
       * 2. Call close() to clean up state (removes .active, body click, focus trap)
       * 3. Restore .animate on next frame so future opens still animate in
       */
      notif.classList.remove('animate');
    }

    var cartNotifEl = document.querySelector('cart-notification');
    if (cartNotifEl && cartNotifEl.close) cartNotifEl.close();

    if (notif) {
      notif.classList.remove('cart-notification--fade-out');
      /* Restore .animate after close so next open animates normally */
      requestAnimationFrame(function () {
        notif.classList.add('animate');
      });
    }

    /* Reset reco state for next open */
    var container = document.getElementById('cart-notification-reco');
    if (container) container.classList.remove('ines-notif-reco--visible');

    _isAdding = false;
  }

  function startAutoClose() {
    clearAutoCloseTimer();
    autoCloseTimer = setTimeout(fadeOutAndClose, AUTO_CLOSE_MS);
  }

  function clearAutoCloseTimer() {
    if (autoCloseTimer) { clearTimeout(autoCloseTimer); autoCloseTimer = null; }
  }

  /**
   * Attach hover listeners to pause/resume auto-close timer.
   * Only binds once per element.
   */
  function initAutoCloseListeners() {
    var notif = document.getElementById('cart-notification');
    if (!notif || notif._autoCloseInit) return;
    notif._autoCloseInit = true;

    notif.addEventListener('mouseenter', clearAutoCloseTimer);
    notif.addEventListener('mouseleave', function () {
      if (!_isAdding) startAutoClose();
    });
    notif.addEventListener('touchstart', clearAutoCloseTimer, { passive: true });
  }

  /* ── Refresh cart icon bubble via section rendering ── */
  function refreshCartBubble() {
    fetch('/?sections=cart-icon-bubble')
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var bubble = document.getElementById('cart-icon-bubble');
        if (bubble && sections['cart-icon-bubble']) {
          var doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
          var src = doc.querySelector('.shopify-section');
          if (src) bubble.innerHTML = src.innerHTML;
        }
      })
      .catch(function () { /* silent fail */ });
  }

  /* ── Update CTA with cart item count ── */
  function updateCtaCount() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var btn = document.getElementById('cart-notification-button');
        if (btn) btn.textContent = 'Voir mon panier (' + cart.item_count + ')';
      })
      .catch(function () { /* silent */ });
  }

  /* ── HTML escape ── */
  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str || ''));
    return div.innerHTML;
  }

  /* ── Attribute escape (for data-product-json inside single quotes) ── */
  function escapeAttr(str) {
    return str.replace(/&/g, '&amp;').replace(/'/g, '&#39;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
})();
